import { create } from '@bufbuild/protobuf';

import * as pbm from '@/grpc-gen/reservation_service_pb';
import * as pbs from '@/grpc-gen/service_manager_service_pb';

import ApiService from '@/model/Service';
import Tab from '@/model/Tab';
import {
  dateFlooredToDate, localDateAsInUnixUTCYMD, unixFromDate, unixUTCAsInLocalDateYMD,
} from '@/services/time-utils';
import Currency from './currency';
import ReservationServiceTranslation from './reservation-service-translation';

export interface ReservationServiceMapping {
  tabsById: Map<number, Tab>,
  currenciesById: Map<number, Currency>,
}

export interface ReservationServiceBlackout {
  blackoutStartDate: Date;
  blackoutEndDate: Date;
}

export default class ReservationService extends ApiService {
  description: string = '';

  tags: string[] = [];

  imageUrl: string = '';

  localStartDate?: Date;

  localEndDate?: Date;

  localStartTime?: string;

  localEndTime?: string;

  minPartySize?: number;

  maxPartySize?: number;

  weekDays: number[] = [];

  tabs: Tab[] = [];

  duration?: number;

  locale?: string;

  code?: string;

  price?: number;

  isPricePerPerson?: boolean;

  currency?: Currency;

  isPromoted: boolean = false;

  isRequired: boolean = false;

  minMinutesInAdvance?: number;

  minMinutesToModify?: number;

  // blackoutStartDate?: Date;

  // blackoutEndDate?: Date;

  maxParallelBookings?: number;

  isActive: boolean = true;

  isDeleted: boolean = false;

  isDeposit: boolean = true;

  blackouts: ReservationServiceBlackout[] = [];

  translations: ReservationServiceTranslation[] = [];

  static fromGrpcResponse(r: pbs.ReservationServiceResponse, m?: ReservationServiceMapping): ReservationService {
    return ReservationService.fromGrpcModel(r.service, m);
  }

  static fromGrpcModel(o?: pbm.ReservationService, m?: ReservationServiceMapping): ReservationService {
    const s = new ReservationService();

    s.id = Number(o?.id ?? 0);
    s.title = o?.title ?? '';
    s.description = o?.description ?? '';
    s.tags = o?.tags ?? [];
    s.imageUrl = o?.imageUrl ?? '';
    s.order = Number(o?.order ?? 0);
    s.localStartDate = localDateAsInUnixUTCYMD(Number(o?.startDate ?? 0));
    s.localEndDate = localDateAsInUnixUTCYMD(Number(o?.endDate ?? 0));
    s.localStartTime = o?.startTime || undefined;
    s.localEndTime = o?.endTime || undefined;
    s.minPartySize = o?.minPartySize || undefined;
    s.maxPartySize = o?.maxPartySize || undefined;
    s.weekDays = o?.weekDay ?? [];
    s.duration = o?.duration || undefined;
    s.locale = o?.locale || undefined;
    s.code = o?.code || undefined;
    s.price = o?.price !== undefined ? Number(o.price) : undefined;
    s.isPricePerPerson = o?.isPricePerPerson || false;
    s.isPromoted = o?.isPromoted ?? false;
    s.isRequired = o?.isRequired ?? false;
    s.minMinutesInAdvance = o?.minMinutesInAdvance || undefined;
    s.minMinutesToModify = o?.minMinutesToModify || undefined;
    // s.blackoutStartDate = localDateAsInUnixUTCYMD(o?.getBlackoutStartDate());
    // s.blackoutEndDate = localDateAsInUnixUTCYMD(o?.getBlackoutEndDate());
    s.maxParallelBookings = o?.maxParallelBookings || undefined;
    s.isActive = o?.isActive ?? true;
    s.isDeleted = o?.isDeleted ?? false;
    s.color = o?.color?.toUpperCase() || '#FF3D94';
    s.isDeposit = o?.isDeposit ?? false;

    s.currency = m?.currenciesById.get(Number(o?.currencyId ?? 0));

    s.weekDays.sort();

    s.blackouts = o?.blackouts?.map((bo) => ({
      blackoutStartDate: localDateAsInUnixUTCYMD(Number(bo?.blackoutStartDate ?? 0)) ?? dateFlooredToDate(new Date()),
      blackoutEndDate: localDateAsInUnixUTCYMD(Number(bo?.blackoutEndDate ?? 0)) ?? dateFlooredToDate(new Date()),
    })) ?? [];

    s.tabs = o?.tabIds?.flatMap((tid) => m?.tabsById.get(Number(tid ?? 0)) ?? []) ?? [];
    s.tabs.sort((t1, t2) => t1.id - t2.id);

    s.translations = o?.translations?.map((t) => ReservationServiceTranslation.fromGrpcModel(t)) ?? [];

    return s;
  }

  toGrpcModel(): pbm.ReservationService {
    return create(pbm.ReservationServiceSchema, {
      id: BigInt(this.id),
      title: this.title ?? '',
      description: this.description ?? '',
      tags: this.tags ?? [],
      imageUrl: this.imageUrl ?? '',
      order: BigInt(this.order),
      startDate: BigInt(this.localStartDate ? unixUTCAsInLocalDateYMD(this.localStartDate)! : 0),
      endDate: BigInt(this.localEndDate ? unixUTCAsInLocalDateYMD(this.localEndDate)! : 0),
      startTime: this.localStartTime ?? '',
      endTime: this.localEndTime ?? '',
      minPartySize: this.minPartySize ?? 0,
      maxPartySize: this.maxPartySize ?? 0,
      weekDay: this.weekDays ?? [],
      duration: this.duration ?? 0,
      locale: this.locale ?? '',
      code: this.code ?? '',
      price: BigInt(this.price ?? 0),
      isPricePerPerson: this.isPricePerPerson ?? false,
      currencyId: BigInt(this.currency?.id ?? 0),
      isPromoted: this.isPromoted ?? false,
      isRequired: this.isRequired ?? false,
      minMinutesInAdvance: this.minMinutesInAdvance ?? 0,
      minMinutesToModify: this.minMinutesToModify ?? 0,
      maxParallelBookings: this.maxParallelBookings ?? 0,
      isActive: this.isActive ?? true,
      isDeleted: this.isDeleted ?? false,
      color: this.color ?? '',
      isDeposit: this.isDeposit ?? false,
      blackouts: (this.blackouts ?? []).map((b) => create(pbm.ReservationServiceBlackoutSchema, {
        blackoutStartDate: BigInt(unixUTCAsInLocalDateYMD(b.blackoutStartDate)!),
        blackoutEndDate: BigInt(unixUTCAsInLocalDateYMD(b.blackoutEndDate)!),
      })),
      tabIds: (this.tabs ?? []).map((t) => BigInt(t.id)),
      translations: (this.translations ?? []).map((t) => t.toGrpcModel()),
    });
  }

  static compare(o1: ReservationService, o2: ReservationService): number {
    const res = o1.order - o2.order;
    if (res !== 0) return res;
    return o1.id - o2.id;
  }

  clone(): ReservationService {
    const copy = new ReservationService();
    Object.assign(copy, this);
    copy.tags = Array.from(this.tags);
    copy.blackouts = Array.from(this.blackouts);
    copy.tabs = Array.from(this.tabs);
    copy.translations = Array.from(this.translations.map((t) => t.clone()));
    return copy;
  }
}
